import { useMemo } from 'react';
import { useRouter } from 'next/router';

import { publicEnv } from '~/features/environment/public';

import { resolveActivePromoCampaign } from '../campaigns/registry';
import type { PromoCampaignContext } from '../campaigns/types';
import { singleQueryParamValueSchema } from '../models/promotions';

import { usePromotion } from './use-promotion';

/**
 * Resolves the first matching promo campaign from the registry using live promotion data
 * and optional `?force_promo=<promotionId>` preview override (non-production only).
 */
export function useActivePromoCampaign() {
  const { query } = useRouter();
  const { data: promotionData, isFetched: promotionFetched } = usePromotion();

  const forcedPromotionId =
    publicEnv.NEXT_PUBLIC_ENVIRONMENT_NAME !== 'production'
      ? singleQueryParamValueSchema.parse(query.force_promo)
      : undefined;

  const ctx: PromoCampaignContext = useMemo(
    () => ({
      forcedPromotionId,
      promoCode: promotionData?.promoCode,
      promotionId: promotionData?.promotionId,
    }),
    [forcedPromotionId, promotionData?.promoCode, promotionData?.promotionId]
  );

  const active = useMemo(() => resolveActivePromoCampaign(ctx), [ctx]);

  const campaign = active?.campaign ?? null;
  const resolved = active?.resolved ?? null;

  const isGateReady = campaign ? campaign.isGateReady({ ...ctx, promotionFetched }) : promotionFetched;

  const slideKey = campaign && resolved != null ? campaign.getSlideKey({ ...ctx, resolved }) : null;

  return {
    campaign,
    resolved,
    promoCode: promotionData?.promoCode,
    promotionId: promotionData?.promotionId,
    forcedPromotionId,
    isGateReady,
    slideKey,
    campaignContext: ctx,
  };
}
